import React, { useMemo, useState } from "react";
import "./SimpleToothChartPicker.scss";

type QuadrantKey = "ur" | "ul" | "lr" | "ll";
type SelectionMode = "custom" | "upper" | "lower" | "full";
type Dentition = "adult" | "child";

const ADULT_QUADRANTS: Record<QuadrantKey, number[]> = {
  ur: [18, 17, 16, 15, 14, 13, 12, 11],
  ul: [21, 22, 23, 24, 25, 26, 27, 28],
  lr: [48, 47, 46, 45, 44, 43, 42, 41],
  ll: [31, 32, 33, 34, 35, 36, 37, 38],
};

/** FDI primary (deciduous) teeth — 20 teeth. */
const CHILD_QUADRANTS: Record<QuadrantKey, number[]> = {
  ur: [55, 54, 53, 52, 51],
  ul: [61, 62, 63, 64, 65],
  lr: [85, 84, 83, 82, 81],
  ll: [71, 72, 73, 74, 75],
};

const DENTITION_OPTIONS: { value: Dentition; label: string }[] = [
  { value: "adult", label: "Adult" },
  { value: "child", label: "Child" },
];

const MODE_OPTIONS: { value: SelectionMode; label: string }[] = [
  { value: "custom", label: "Custom" },
  { value: "upper", label: "Upper" },
  { value: "lower", label: "Lower" },
  { value: "full", label: "Full" },
];

const MODE_TOKENS = new Set(["full", "upper", "lower"]);

const toSortedStrings = (nums: number[]) =>
  nums.map(String).sort((a, b) => Number(a) - Number(b));

function chartFor(dentition: Dentition) {
  const quadrants = dentition === "child" ? CHILD_QUADRANTS : ADULT_QUADRANTS;
  const upper = [...quadrants.ur, ...quadrants.ul];
  const lower = [...quadrants.lr, ...quadrants.ll];
  const full = [...upper, ...lower];
  return {
    quadrants,
    upper: toSortedStrings(upper),
    lower: toSortedStrings(lower),
    full: toSortedStrings(full),
    fullCount: full.length,
    halfCount: upper.length,
  };
}

function sameTeeth(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((t) => set.has(t));
}

function isPrimaryTooth(n: number) {
  return n >= 51 && n <= 85;
}

function inferDentition(value: string[]): Dentition {
  if (!value.length) return "adult";
  if (value.length === 1 && MODE_TOKENS.has(value[0].toLowerCase())) {
    return "adult";
  }
  const nums = value.map(Number).filter((n) => Number.isFinite(n));
  if (!nums.length) return "adult";
  const primary = nums.filter(isPrimaryTooth).length;
  return primary >= nums.length / 2 ? "child" : "adult";
}

function inferMode(value: string[], dentition: Dentition): SelectionMode {
  if (value.length === 1) {
    const token = value[0].toLowerCase();
    if (token === "full" || token === "upper" || token === "lower") {
      return token;
    }
  }
  const chart = chartFor(dentition);
  // Legacy: previously stored every tooth number for full/upper/lower
  if (sameTeeth(value, chart.full)) return "full";
  if (sameTeeth(value, chart.upper)) return "upper";
  if (sameTeeth(value, chart.lower)) return "lower";
  return "custom";
}

/** Value stored for mode — token only, not every tooth. */
function teethForMode(mode: SelectionMode): string[] {
  if (mode === "full") return ["full"];
  if (mode === "upper") return ["upper"];
  if (mode === "lower") return ["lower"];
  return [];
}

/** Expand mode token (or legacy full lists) to tooth numbers for chart highlight. */
function expandTeethForChart(value: string[], dentition: Dentition): string[] {
  if (value.length === 1) {
    const token = value[0].toLowerCase();
    const chart = chartFor(dentition);
    if (token === "full") return chart.full;
    if (token === "upper") return chart.upper;
    if (token === "lower") return chart.lower;
  }
  return value;
}

function chartTeethForMode(mode: SelectionMode, dentition: Dentition): string[] {
  const chart = chartFor(dentition);
  if (mode === "full") return chart.full;
  if (mode === "upper") return chart.upper;
  if (mode === "lower") return chart.lower;
  return [];
}

interface SimpleToothChartPickerProps {
  value: string[];
  onChange: (teeth: string[]) => void;
  /** At least one tooth must remain selected. Default true. */
  required?: boolean;
  /** Show invalid styling / message (e.g. after failed submit). */
  error?: boolean;
  /** Optional extra control rendered at the end of the toolbar row. */
  toolbarExtra?: React.ReactNode;
}

function SimpleToothChartPicker({
  value,
  onChange,
  required = true,
  error = false,
  toolbarExtra,
}: SimpleToothChartPickerProps) {
  const [dentition, setDentition] = useState<Dentition>(() =>
    inferDentition(value)
  );
  const [mode, setMode] = useState<SelectionMode>(() =>
    value.length ? inferMode(value, inferDentition(value)) : "custom"
  );

  const chart = useMemo(() => chartFor(dentition), [dentition]);
  const chartSelected = useMemo(
    () => expandTeethForChart(value, dentition),
    [value, dentition]
  );
  const selectedSet = useMemo(() => new Set(chartSelected), [chartSelected]);
  const isEmpty = value.length === 0;
  const showError = Boolean(error);

  const applyDentition = (next: Dentition) => {
    if (next === dentition) return;
    setDentition(next);
    setMode("custom");
    onChange([]);
  };

  const applyMode = (next: SelectionMode) => {
    if (next === "custom") {
      if (mode !== "custom") {
        setMode("custom");
        onChange([]);
      }
      return;
    }
    setMode(next);
    onChange(teethForMode(next));
  };

  const toggleTooth = (toothNo: number) => {
    const key = String(toothNo);
    const base =
      mode === "full" || mode === "upper" || mode === "lower"
        ? chartTeethForMode(mode, dentition)
        : value;
    setMode("custom");

    if (base.includes(key)) {
      if (required && base.length <= 1) return;
      onChange(base.filter((t) => t !== key));
      return;
    }
    onChange([...base, key].sort((a, b) => Number(a) - Number(b)));
  };

  const renderTooth = (toothNo: number, arch: "upper" | "lower") => {
    const selected = selectedSet.has(String(toothNo));
    const isUpper = arch === "upper";

    return (
      <button
        key={toothNo}
        type="button"
        onClick={() => toggleTooth(toothNo)}
        className={`simple-tooth-chart__tooth ${isUpper ? "is-upper" : "is-lower"} ${
          selected ? "is-selected" : ""
        }`}
        title={`Tooth ${toothNo}`}
        aria-pressed={selected}
      >
        {isUpper && (
          <span className="simple-tooth-chart__number">{toothNo}</span>
        )}
        <span className="simple-tooth-chart__crown" aria-hidden />
        {!isUpper && (
          <span className="simple-tooth-chart__number">{toothNo}</span>
        )}
      </button>
    );
  };

  const renderQuadrant = (key: QuadrantKey, arch: "upper" | "lower") => (
    <div className={`simple-tooth-chart__quadrant simple-tooth-chart__quadrant--${key}`}>
      {chart.quadrants[key].map((toothNo) => renderTooth(toothNo, arch))}
    </div>
  );

  const summaryLabel =
    mode === "full"
      ? `Full mouth (${chart.fullCount})`
      : mode === "upper"
        ? `Upper arch (${chart.halfCount})`
        : mode === "lower"
          ? `Lower arch (${chart.halfCount})`
          : value.length > 0
            ? `${value.length} tooth${value.length > 1 ? "s" : ""} selected — click to toggle`
            : "Select teeth individually";

  return (
    <div
      className={`simple-tooth-chart${showError ? " is-invalid" : ""}`}
      aria-invalid={showError || undefined}
    >
      <div className="simple-tooth-chart__header">
        <div className="simple-tooth-chart__toolbar">
          <div
            className="simple-tooth-chart__dentition"
            role="group"
            aria-label="Dentition"
          >
            {DENTITION_OPTIONS.map(({ value: option, label }) => (
              <button
                key={option}
                type="button"
                onClick={() => applyDentition(option)}
                className={`simple-tooth-chart__dentition-btn${
                  dentition === option ? " is-active" : ""
                }`}
                aria-pressed={dentition === option}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="simple-tooth-chart__modes" role="group" aria-label="Tooth selection mode">
            {MODE_OPTIONS.map(({ value: option, label }) => (
              <button
                key={option}
                type="button"
                onClick={() => applyMode(option)}
                className={`simple-tooth-chart__mode${
                  mode === option ? " is-active" : ""
                }`}
                aria-pressed={mode === option}
              >
                {label}
              </button>
            ))}
          </div>

          {toolbarExtra && (
            <div className="simple-tooth-chart__toolbar-extra">{toolbarExtra}</div>
          )}
        </div>

        <p
          className={`simple-tooth-chart__hint${showError ? " is-error" : ""}`}
        >
          {showError && isEmpty ? "Select at least one tooth" : summaryLabel}
          {required ? (
            <span className="simple-tooth-chart__required" aria-hidden>
              *
            </span>
          ) : null}
        </p>
        {mode === "custom" && value.length > 0 && (
          <div className="simple-tooth-chart__chips">
            {value.map((tooth) => (
              <span key={tooth} className="simple-tooth-chart__chip">
                {tooth}
              </span>
            ))}
            {!required && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="simple-tooth-chart__clear"
              >
                Clear all
              </button>
            )}
          </div>
        )}
        {(mode === "full" || mode === "upper" || mode === "lower") && (
          <div className="simple-tooth-chart__chips">
            <span className="simple-tooth-chart__chip">
              {mode === "full" ? "full" : mode === "upper" ? "upper" : "lower"}
            </span>
          </div>
        )}
      </div>

      <div
        className={`simple-tooth-chart__body${
          dentition === "child" ? " is-child" : ""
        }`}
      >
        <span className="simple-tooth-chart__label simple-tooth-chart__label--ur">
          UR
        </span>
        <span className="simple-tooth-chart__label simple-tooth-chart__label--ul">
          UL
        </span>
        <span className="simple-tooth-chart__label simple-tooth-chart__label--lr">
          LR
        </span>
        <span className="simple-tooth-chart__label simple-tooth-chart__label--ll">
          LL
        </span>

        <div className="simple-tooth-chart__midline" aria-hidden />

        <div className="simple-tooth-chart__arch simple-tooth-chart__arch--upper">
          {renderQuadrant("ur", "upper")}
          {renderQuadrant("ul", "upper")}
        </div>

        <div className="simple-tooth-chart__occlusal" aria-hidden />

        <div className="simple-tooth-chart__arch simple-tooth-chart__arch--lower">
          {renderQuadrant("lr", "lower")}
          {renderQuadrant("ll", "lower")}
        </div>
      </div>
    </div>
  );
}

export default SimpleToothChartPicker;
